import { isProd } from 'utils/env';
import { getCookie } from 'utils/cookies';

import { SEGMENT_DEBUG_LOGGER_ENABLED_COOKIE_KEY } from './constants';

export type Segment = {
  page: () => void;
  identify: (userId: string, context: Record<any, any>) => void;
  track: (
    eventName: string,
    event: Record<any, any>,
    options: Record<any, any>,
    callback?: () => void,
  ) => void;
};

declare global {
  interface Window {
    analytics: Segment | void[] | void;
  }
}

const getSegment = () =>
  !!window.analytics && !Array.isArray(window.analytics) ? window.analytics : null;

/**
 * Determines whether or not the Segment debug logger is enabled based
 * on a JS-accessible cookie. Set a cookie with the key stored in
 * `SEGMENT_DEBUG_LOGGER_ENABLED_COOKIE_KEY` and a value of `"true"`
 * to enable debug logging.
 *
 * For ease of use, the following bookmarklet allows us to toggle
 * the Segment debug logger:
 *
 * ```javascript
 * javascript:(function(){
 *   confirm('Toggle Segment Logs: Cancel=Disable, OK=Enable')
 *     ? (document.cookie="SEGMENT_DEBUG_LOGGER_ENABLED=true") && alert('Enabled')
 *     : (document.cookie="SEGMENT_DEBUG_LOGGER_ENABLED=false") && alert('Disabled')
 * })();
 * ```
 */
const isSegmentDebugLoggerEnabled = () =>
  getCookie(SEGMENT_DEBUG_LOGGER_ENABLED_COOKIE_KEY) === 'true';

/**
 * Prints debug details about a Segment SDK call to the JS console. Logging
 * is supressed if the Segment debug logger cookie is not set to `"true"`.
 *
 * For more info, see `isSegmentDebugLoggerEnabled`.
 */
const printSegmentDebugLog = (...args: any[]) =>
  isSegmentDebugLoggerEnabled() && console.log(...args);

/**
 * Segment JS SDK method for tracking an event.
 *
 * For more info on enabling debug logging for development, see `isSegmentDebugLoggerEnabled`.
 *
 * More info: https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#track
 */
export const track = (
  eventName: string,
  eventProperties: { groupId?: string },
  options?: Record<any, any>,
  callback?: () => void,
) => {
  const segment = getSegment();

  printSegmentDebugLog(
    '[SEGMENT][TRACK]',
    eventName,
    eventProperties,
    {
      context: { groupId: eventProperties.groupId },
    },
    options,
    callback,
  );

  if (isProd() && segment) {
    // For prod, actually trigger Segment's .track() method
    segment.track(
      eventName,
      eventProperties,
      {
        ...options,
        context: { groupId: eventProperties.groupId },
      },
      callback,
    );
  } else if (callback) {
    // For non-prod, log the track call to the console and manually trigger the callback
    callback();
  }
};

/**
 * Segment JS SDK method for identifying the current page.
 *
 * For more info on enabling debug logging for development, see `isSegmentDebugLoggerEnabled`.
 *
 * More info: https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#page
 */
export const page = () => {
  const segment = getSegment();
  printSegmentDebugLog('[SEGMENT][PAGE] Page tracked');

  if (isProd() && segment) {
    segment.page();
  }
};

/**
 * Segment JS SDK method for identifying the current user.
 *
 * For more info on enabling debug logging for development, see `isSegmentDebugLoggerEnabled`.
 *
 * More info: https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#identify
 */
export const identify = (userId: string, userContext: Record<any, any>) => {
  const segment = getSegment();
  printSegmentDebugLog('[SEGMENT][IDENTIFY]', userId, userContext);

  if (isProd() && segment) {
    segment.identify(userId, userContext);
  }
};

/**
 * Method to determine whether or not analytics.js has loaded. Before analytics.js is loaded,
 * `window.analytics` is an array that queues tracked events until the script is loaded. Once
 * the script is loaded, queued track events are sent to Segment and `window.analytics` is
 * converted from an array to an object.
 *
 * If the emitter is not initialized, then callback functions on Segment SDK methods will not
 * be executed. Therefore, use this function to determine if it is safe to use Segment SDK
 * method callbacks.
 */
export const isEmitterInitialized = () => !!getSegment();

export default { track, page, identify };
